import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'

import { useGetCuttingTechnologiesQuery } from '@/app/services/cuttingTechnologies'
import { useGetMaterialsQuery } from '@/app/services/materials'
import { useGetRatesQuery } from '@/app/services/rates'
import { useGetRateTablesQuery } from '@/app/services/rateTables'
import { useGetSheetsQuery } from '@/app/services/sheets'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { isProfileSizeFit, thicknessSameWithTolerance } from '@/common/utils/SheetUtils/SheetUtils'

const sortMaterials = (materials) =>
    materials.sort((a, b) => a.materialName.toLocaleLowerCase().localeCompare(b.materialName.toLocaleLowerCase()))

/**
 * Custom hook to manage order item property options
 * @param {Object} params - The parameters object
 * @param {string} params.cuttingTechnologyId - Selected cutting technology ID
 * @param {string} params.materialId - Selected material ID
 * @param {string} params.mode - Mode (flat/rotary)
 * @param {Object} params.partDimensions - Part dimensions
 * @param {number} params.thickness - Selected thickness
 */
export function useOrderItemPropertyOptions({ cuttingTechnologyId, materialId, mode, partDimensions, thickness }) {
    const { t } = useTranslation()
    const organisationId = useSelector(selectOrganisationId)
    const organisation = useSelector(selectOrganisation)

    // Fetch data
    const materials = useGetMaterialsQuery({ organisationId })
    const sheets = useGetSheetsQuery({ organisationId })
    const cuttingTech = useGetCuttingTechnologiesQuery({ organisationId })
    const rateTables = useGetRateTablesQuery({ organisationId })
    const rates = useGetRatesQuery({ organisationId })

    // Validate rate table thickness
    const sheetHasRateTableThickness = useCallback(
        (sheet, rateTable) => {
            if (!sheet || !rateTable || !rates.data) return false

            const sortedRates = Object.values(rates.data)
                .filter((rate) => rate.rateTableId === rateTable.rateTableId && !rate.isDeleted)
                .sort((a, b) => a.thickness - b.thickness)

            return (
                sortedRates.length > 0 &&
                sheet.thickness >= sortedRates[0].thickness &&
                sheet.thickness <= sortedRates[sortedRates.length - 1].thickness
            )
        },
        [rates.data]
    )

    // Sheet filtering logic
    const filterSheet = useCallback(
        (sheet) => {
            if (!sheet) return false

            try {
                const cuttingTech = sheet?.sheetCuttingTechnology?.find(
                    (tech) => tech?.cuttingTechnologyId === cuttingTechnologyId
                )
                return (
                    sheet.isWebStoreEnabled &&
                    !sheet.isDeleted &&
                    (cuttingTech?.isEnabled ?? true) &&
                    (sheet.expiryDate ? !dayjs(sheet.expiryDate).isBefore(dayjs(), 'day') : true) &&
                    (partDimensions ? isProfileSizeFit(sheet, partDimensions, organisation) : true)
                )
            } catch (error) {
                console.error('Error filtering sheet:', error)
                return false
            }
        },
        [cuttingTechnologyId, partDimensions, organisation]
    )

    // Computed options
    const grainDirectionOptions = useMemo(
        () => [
            { label: t('Any (best price)'), value: 'None' },
            { label: t('Horizontal'), value: 'Horizontal' },
            { label: t('Vertical'), value: 'Vertical' },
        ],
        [t]
    )

    const cuttingTechOptions = useMemo(
        () =>
            cuttingTech.data
                ? Object.values(cuttingTech.data).filter(
                      (tech) => tech?.type?.toLowerCase() === mode && !tech?.isDeleted
                  )
                : [],
        [cuttingTech.data, mode]
    )

    const materialOptions = useMemo(() => {
        if (!materials.data || !rateTables.data || !sheets.data) return []

        const materialRateTableFilter = (materialRateTable) =>
            rateTables.data[materialRateTable.rateTableId]?.cuttingTechnologyId === cuttingTechnologyId

        return sortMaterials(
            Object.values(materials.data).filter((material) => {
                const basicCheck =
                    material.materialRateTables.some(materialRateTableFilter) &&
                    !material.isDeleted &&
                    material.sheetsCount > 0 &&
                    material.type.toLowerCase() === mode

                if (!basicCheck) return false

                const sheetsForMaterial = material?.sheetIds.map((sheetId) => sheets.data[sheetId]).filter(filterSheet)

                return sheetsForMaterial && sheetsForMaterial.length > 0
            })
        )
    }, [materials.data, rateTables.data, cuttingTechnologyId, mode, filterSheet, sheets.data])

    const thicknessOptions = useMemo(() => {
        if (!materials.data || !sheets.data) return []

        const material = materials.data[materialId]
        const materialRateTable = material?.materialRateTables.find(
            (rateTable) => rateTables.data[rateTable.rateTableId]?.cuttingTechnologyId === cuttingTechnologyId
        )

        const sheetsForMaterial = material?.sheetIds.map((sheetId) => sheets.data[sheetId]).filter(filterSheet)
        const filteredSheets = sheetsForMaterial?.filter((sheet) =>
            sheetHasRateTableThickness(sheet, rateTables.data[materialRateTable?.rateTableId])
        )

        const thicknesses = filteredSheets?.map((sheet) => sheet.thickness).sort((a, b) => a - b)
        return [...new Set(thicknesses)]
    }, [
        materialId,
        materials.data,
        sheets.data,
        filterSheet,
        rateTables.data,
        cuttingTechnologyId,
        sheetHasRateTableThickness,
    ])

    const sheetOptions = useMemo(() => {
        if (!materials.data || !sheets.data) return []

        const material = materials.data[materialId]
        const sheetsForMaterial = material?.sheetIds
            .map((sheetId) => sheets.data[sheetId])
            .filter((sheet) => sheet.thickness === thickness)
            .filter(filterSheet)
            .filter((sheet) =>
                mode === 'flat'
                    ? sheet.thickness === thickness
                    : partDimensions
                      ? thicknessSameWithTolerance(sheet, thickness, partDimensions, organisation)
                      : true
            )

        return sheetsForMaterial?.map((sheet) => ({
            sheetId: sheet.sheetId,
            sheetLength: sheet.materialLength,
            sheetWidth: sheet.sheetWidth,
            sheetHeight: sheet.sheetHeight,
            sheetSize: `${sheet.sheetWidth} x ${sheet.sheetHeight}`,
            sheetCost: sheet.sheetCost,
            sheetRatePrice: sheet.sheetRatePrice,
            expiryDate: sheet.expiryDate,
            isSoldByOrganisation: sheet.isSoldByOrganisation,
            defaultMaterialConsumptionMode: sheet.defaultMaterialConsumptionMode,
        }))
    }, [materialId, materials.data, sheets.data, filterSheet, thickness, mode, partDimensions, organisation])

    const isLoading =
        materials.isLoading || sheets.isLoading || cuttingTech.isLoading || rateTables.isLoading || rates.isLoading

    const error = materials.error || sheets.error || cuttingTech.error || rateTables.error || rates.error

    return {
        cuttingTechOptions,
        materialOptions,
        thicknessOptions,
        sheetOptions,
        grainDirectionOptions,
        isLoading,
        error,
    }
}
